import type { ReactNode } from 'react';
import type { DestinationId } from '@d-orca/shared/navigation';
import {
  CalendarIcon,
  ChartColumnIcon,
  ChartPieIcon,
  CpuIcon,
  FileStackIcon,
  GaugeIcon,
  LogisticsIcon,
  MonitorIcon,
  PortfolioIcon,
} from '@utils/icons';

export interface IDestinationMeta {
  /** The page's own name — the sidebar's key, so a link and the nav never call a page two things. */
  titleKey: string;
  icon: ReactNode;
  /**
   * The params a chip names, each by the i18n key its value is read under:
   * `key.<value>` for a value or each member of a list, `key` itself for a
   * flag. A param absent here (an id) is named by the link's caption instead.
   */
  facets?: Readonly<Record<string, string>>;
}

const ICON_SIZE = 14;

/**
 * What the frontend knows about each destination that the shared catalog must
 * not: a name to show and an icon. A `Record` over the catalog's ids, so a
 * destination added there without a name here is a compile error, not a chip
 * reading `undefined`. The icons are the sidebar's (`Sidebar/nav-config.tsx`).
 */
export const DESTINATION_META: Readonly<Record<DestinationId, IDestinationMeta>> = {
  dashboard: { titleKey: 'nav.item.dashboard.title', icon: <PortfolioIcon size={ICON_SIZE} /> },
  monitor: { titleKey: 'nav.item.monitor.title', icon: <MonitorIcon size={ICON_SIZE} /> },
  activities: {
    titleKey: 'nav.item.activitiesIssues.title',
    icon: <CalendarIcon size={ICON_SIZE} />,
    facets: { types: 'aiAgent.link.types', status: 'activities.status', critical: 'activities.filter.critical' },
  },
  scheduler: { titleKey: 'nav.item.scheduler.title', icon: <ChartColumnIcon size={ICON_SIZE} /> },
  documents: { titleKey: 'nav.item.documents.title', icon: <FileStackIcon size={ICON_SIZE} /> },
  logistics: { titleKey: 'nav.item.logistics.title', icon: <LogisticsIcon size={ICON_SIZE} /> },
  analytics: { titleKey: 'nav.item.analytics.title', icon: <ChartPieIcon size={ICON_SIZE} /> },
  productivity: { titleKey: 'nav.item.productivity.title', icon: <GaugeIcon size={ICON_SIZE} /> },
  equipment: { titleKey: 'nav.item.equipment.title', icon: <CpuIcon size={ICON_SIZE} /> },
};
